import BannerService from '@engoo/banner-service';
import ServerTime from '@engoo/server-time';
import TranscoderService from '@engoo/transcoder-service';
import { fetchTimeFromServer } from 'app/components/utilities/ServerTime';
import { QueryParameter } from 'app/constants/QueryParameter';
import { Brand, Organization } from 'app/constants/schemas';
import {
  init as realInitAuth,
  watchStorage as watchAuth,
  SiblingTabTimeoutError,
} from 'app/models/auth/actions';
import { getMatchedDomain, getSiteBrand } from 'app/models/env/brand';
import { init as realInitI18n, PersistedLocalizationService } from 'app/models/i18n/actions';
import { init as realInitLocalStorage } from 'app/models/localStorage/actions';
import { removeLanguageParam } from 'app/models/route/actions';
import { getLanguageQueryParam } from 'app/models/route/selectors';
import { init as realInitSessionStorage } from 'app/models/sessionStorage/actions';
import {
  checkBrokenProxyStatus,
  clientVersionListener as realClientVersionListener,
  makeErrorHandlerWithClientChecking,
} from 'app/remote/client/actions';
import { createBannerService } from 'app/services/bannerService';
import { setBasePath } from 'app/services/basePath';
import { ICookieInteractions, makeCookieInteractions } from 'app/services/cookies';
import * as ErrorReporter from 'app/services/errorReporter';
import { History } from 'app/services/history';
import { createTranscoderService } from 'app/services/transcoderService';
import { AppThunk } from 'app/store';
import {
  addLocalStorageListener,
  getWindow,
} from 'app/utils/env';
import { dispatchToDiagnosticService } from 'app/utils/errorReporter';
import QueryString from 'query-string';
import { getEntityOfTypeOrThrow } from '../entities/selectors';
import { getDefaultDomain } from '../entities/selectors/brands';
import FluentdLogger from 'app/services/fluentdLogger';
import Logger from 'app/services/logger';

export interface IInitOptions {
  clientVersionListener?: typeof realClientVersionListener;
  disableClientChecking?: boolean;
  historyService: History;
  initAuth?: typeof realInitAuth;
  initI18n?: typeof realInitI18n;
  initLocalStorage?: typeof realInitLocalStorage;
  initSessionStorage?: typeof realInitSessionStorage;
  serverTime?: ServerTime;
  addLoadEventListener?: (callback: () => unknown) => void;
}

type InitAction = AppThunk<Promise<{
  cookieInterface: ICookieInteractions,
  brand: Brand,
  bannerService: BannerService,
  localizationService: PersistedLocalizationService,
  serverTime: ServerTime,
  transcoderService: TranscoderService,
}>>;

export const init = ({
  clientVersionListener = realClientVersionListener,
  disableClientChecking = false,
  historyService,
  initAuth = realInitAuth,
  initI18n = realInitI18n,
  initLocalStorage = realInitLocalStorage,
  initSessionStorage = realInitSessionStorage,
  serverTime = new ServerTime(),
  addLoadEventListener = (callback) => getWindow()?.addEventListener('load', () => callback()),
}: IInitOptions): InitAction =>
  async (dispatch, getState) => {
    const brand = await getSiteBrand({
      history: historyService,
      dispatch,
      getState,
    });
    const matchedDomain = getMatchedDomain(getState(), brand.id, historyService.location.hostname);
    setBasePath(matchedDomain.pathPrefix);

    const organization = getEntityOfTypeOrThrow(getState(), Organization, brand.organization);

    await Promise.all([
      dispatch(initSessionStorage()),
      dispatch(initLocalStorage()),
    ]);

    addLocalStorageListener(event => {
      dispatch(watchAuth(event)).catch((error) => {
        // Catch to prevent unhandledrejection
        if (error instanceof SiblingTabTimeoutError) {
          // Don't send SiblingTabTimeoutError to Honeybadger because it is not
          // actionable (it probably just represents a temporary network issue).
          Logger.error('Sibling tab timeout:', error);
          FluentdLogger.info({
            name: error.name,
            message: error.message,
          });
        } else {
          ErrorReporter.notify(error, { name: 'Init.watchAuth' });
        }
      });
    });

    if (!disableClientChecking) {
      // Unsubscribe the old listener to handle error reporting and put on one that takes the client
      // version into consideration and has access to dispatch.
      const errorHandler = makeErrorHandlerWithClientChecking(dispatchToDiagnosticService);
      ErrorReporter.subscribe((...args: unknown[]) => dispatch(errorHandler(...args)));

      // Unsubscribe after setting up the new error handler, that way if an error happens here there
      // be a chance of catching it.
      ErrorReporter.unsubscribe(dispatchToDiagnosticService);

      // In safari, checking before the environment loads may create a false positive. Wait for the
      // window to load before checking if the user is behind a proxy.  NOTE: This code operates
      // under the assumption that this script is loaded synchronously on window load. If that is
      // not the case, then it is impossible to know if window had loaded.  Checking document ready
      // state also does not make a difference.
      addLoadEventListener(() => dispatch(checkBrokenProxyStatus()));

      // Start listening for the client version.
      dispatch(clientVersionListener());
    }

    // Set the locale with a URL parameter `lang`. This is mainly used for email callbacks such as
    // password reset. This allows us to display the page in user's chosen language even when the
    // user is not logged in when accessing the page.
    const localizationService = dispatch(initI18n({
      initLocale: getLanguageQueryParam(historyService),
      defaultLocale: brand.defaultLanguage.id,
      fallbackTimezone: brand.fallbackTimezone.name,
    }));

    // To avoid the URL being shared easily, the `lang` parameter needs to be removed immediately
    // after being used.
    if (QueryParameter.Language in QueryString.parse(historyService.location.search)) {
      dispatch(removeLanguageParam(historyService));
    }

    const defaultDomain = getDefaultDomain(getState(), brand.id);
    const cookieInterface = makeCookieInteractions({
      isFirstPartyDmm: brand.firstPartyDmm,
      isFirstParty: organization.firstParty,
      defaultDomain,
    });
    await Promise.all([
      serverTime.setTimeByRequest(fetchTimeFromServer),
      dispatch(initAuth({ localizationService, cookieInterface })),
    ]);
    serverTime.autoRefresh(fetchTimeFromServer, { refreshIntervalMs: 10000 });

    const bannerService = createBannerService();
    const transcoderService = createTranscoderService();

    return { cookieInterface, brand, bannerService, localizationService, serverTime, transcoderService };
  };
